/**
 * Settings view — F12 (authority gate).
 * API keys, agent config, notification preferences.
 */

import { useState, useEffect } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

export function SettingsView({ isOperator }: { isOperator: boolean }) {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [newKeyName, setNewKeyName] = useState("");

  useEffect(() => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }
    clients.control.getSettings()
      .then((res: any) => setSettings(applyMembrane(res, isOperator)))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    // Fetch API keys (via envoy proxy)
    const token = localStorage.getItem("rida_portal_jwt");
    if (token) {
      fetch("/api/envoy/billing/keys", {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      })
        .then(r => r.json())
        .then(d => { if (d?.ok) setApiKeys(d.result?.keys || []); })
        .catch(() => {});
    }
  }, [isOperator]);

  async function save(newSettings: any) {
    const clients = getClients();
    if (!clients) return;
    setSaving(true);
    try {
      await clients.control.updateSettings(newSettings);
      setSettings(applyMembrane(newSettings, isOperator));
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="loading">Loading settings…</div>;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("F12", isOperator)}</span>
        <h1>Settings</h1>
      </div>
      {error && <div className="error-state">{error}</div>}
      <div className="card">
        <div className="card-title">Agent Configuration</div>
        <div className="object-fields">
          <div className="object-field">
            <span className="object-field-label">Agent mode</span>
            <span className="object-field-value">{settings?.agent_mode || "autonomous"}</span>
          </div>
          <div className="object-field">
            <span className="object-field-label">Model</span>
            <span className="object-field-value">{settings?.model || "default"}</span>
          </div>
          <div className="object-field">
            <span className="object-field-label">Notification email</span>
            <span className="object-field-value">{settings?.notification_email || "—"}</span>
          </div>
        </div>
        <button className="btn-secondary" onClick={() => save({ ...settings, agent_mode: settings?.agent_mode === "autonomous" ? "manual" : "autonomous" })} disabled={saving}>
          {saving ? "Saving…" : `Switch to ${settings?.agent_mode === "autonomous" ? "manual" : "autonomous"} mode`}
        </button>
      </div>

      {/* Authority section — what requires human approval */}
      <div className="card">
        <div className="card-title">{applyMembrane("Authority", isOperator)}</div>
        <p className="card-meta" style={{ marginBottom: 10 }}>
          {applyMembrane("These actions always require explicit authorization:", isOperator)}
        </p>
        <div className="object-fields">
          <div className="object-field">
            <span className="object-field-label">External send</span>
            <span className="object-field-value"><span className="badge" data-status="inactive"> gated</span></span>
          </div>
          <div className="object-field">
            <span className="object-field-label">DNS changes</span>
            <span className="object-field-value"><span className="badge" data-status="inactive"> gated</span></span>
          </div>
          <div className="object-field">
            <span className="object-field-label">Payment</span>
            <span className="object-field-value"><span className="badge" data-status="inactive"> gated</span></span>
          </div>
          <div className="object-field">
            <span className="object-field-label">Deploy</span>
            <span className="object-field-value"><span className="badge" data-status="inactive"> gated</span></span>
          </div>
        </div>
      </div>

      {/* API key management */}
      <div className="card">
        <div className="card-title">{applyMembrane("API Keys", isOperator)}</div>
        {apiKeys.length === 0 ? (
          <div className="empty-state">{applyMembrane("No API keys yet.", isOperator)}</div>
        ) : (
          <table className="data-table">
            <thead><tr><th>Name</th><th>Key</th><th>Status</th></tr></thead>
            <tbody>
              {apiKeys.map((key, i) => (
                <tr key={i}>
                  <td>{key.name || "—"}</td>
                  <td className="mono">{key.key_prefix || "grk_"}…</td>
                  <td><span className="badge" data-status={key.status || "active"}>{key.status || "active"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="text-input"
            placeholder="Key name"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            style={{ flex: 1 }}
          />
          <button
            className="btn-secondary"
            onClick={async () => {
              const token = localStorage.getItem("rida_portal_jwt");
              try {
                const res = await fetch("/api/envoy/billing/keys", {
                  method: "POST",
                  headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({ name: newKeyName }),
                });
                const body = await res.json();
                if (body.ok) {
                  setApiKeys([...apiKeys, body.result]);
                  setNewKeyName("");
                }
              } catch {}
            }}
          >
            Create Key
          </button>
        </div>
      </div>
    </div>
  );
}
